/**
 * Resolves the governed Creative Performance projection for one platform.
 *
 * The scope this hook receives is the scope the page is already showing —
 * advertiser, platform and the selected reporting window — so the creatives
 * below the KPIs always describe the same window as the KPIs above them. A
 * creative section quietly reporting a different date range than the page it
 * sits in is worse than no section at all.
 *
 * The request is skipped entirely for platforms with no creative-reporting
 * contract, so an unsupported page costs nothing and still renders its governed
 * unavailable state from the declared reason.
 */

import { useEffect, useState } from "react";

import { CREATIVE_PERFORMANCE_STATE } from "./creativePerformanceModel";

const DEFAULT_PRODUCTION_BACKEND_URL = "https://cphere-backend.onrender.com";
const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (process.env.NODE_ENV === "production" ? DEFAULT_PRODUCTION_BACKEND_URL : "");

/*
 * Both apps read the same union of keys. Cortex and Quantum are served from
 * different origins, so a browser only ever holds one of `clientAuthToken` and
 * `superadminToken` — which makes the order immaterial and lets this module
 * stay byte-identical across the two repositories.
 */
function getAuthToken() {
  return (
    localStorage.getItem("clientAuthToken") ||
    localStorage.getItem("superadminToken") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    ""
  );
}

export default function useCreativePerformance({
  advertiserId,
  platformKey,
  startDate = null,
  endDate = null,
  sort = null,
  enabled = true,
}) {
  const [state, setState] = useState({
    status: CREATIVE_PERFORMANCE_STATE.IDLE,
    projection: null,
  });

  useEffect(() => {
    if (!enabled || !advertiserId || !platformKey) {
      setState({ status: CREATIVE_PERFORMANCE_STATE.IDLE, projection: null });
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    setState({ status: CREATIVE_PERFORMANCE_STATE.LOADING, projection: null });

    (async () => {
      try {
        const params = new URLSearchParams({
          advertiserId: String(advertiserId),
          platformKey: String(platformKey),
        });
        if (startDate) params.set("startDate", startDate);
        if (endDate) params.set("endDate", endDate);
        if (sort) params.set("sort", sort);

        const token = getAuthToken();
        const response = await fetch(
          `${String(API_BASE_URL).replace(/\/+$/, "")}/api/creative-assets/creative-performance?${params}`,
          {
            signal: controller.signal,
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: "include",
          }
        );

        if (!response.ok) throw new Error(`Request failed (${response.status})`);
        const payload = await response.json();
        if (cancelled || controller.signal.aborted) return;

        setState({
          // The projection carries its own governed state; the hook only
          // reports the states the payload cannot describe.
          status: CREATIVE_PERFORMANCE_STATE.REPORTING,
          projection: payload,
        });
      } catch (error) {
        if (cancelled || controller.signal.aborted || error?.name === "AbortError") return;
        setState({ status: CREATIVE_PERFORMANCE_STATE.ERROR, projection: null });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [advertiserId, platformKey, startDate, endDate, sort, enabled]);

  return state;
}
